import baseApi from ".";
import createQueryFn from "../createQueryFn";
import api from "api";
import { GetAllCompaniesRequest, GetAllCompaniesResponse } from "api/companies/getAllCompanies";
import { CreateCompanyRequest, CreateCompanyResponse } from "api/companies/createCompany";
import { GetCompanyByIdRequest, GetCompanyByIdResponse } from "api/companies/getCompanyById";
import { UpdateCompanyRequest, UpdateCompanyResponse } from "api/companies/updateCompany";
import { DeleteCompanyRequest, DeleteCompanyResponse } from "api/companies/deleteCompany";
import { AddUserToCompanyRequest, AddUserToCompanyResponse } from "api/companies/users/addUserToCompany";
import {
  RemoveUserFromCompanyRequest,
  RemoveUserFromCompanyResponse,
} from "api/companies/users/removeUserFromCompany";
import { GetLifecycleEventsRequest, GetLifecycleEventsResponse } from "api/companies/getLifecycleEvents";
import { ApproveCompanyRequest, ApproveCompanyResponse } from "api/companies/approveCompany";
import { DenyCompanyRequest, DenyCompanyResponse } from "api/companies/denyCompany";
import { ArchiveCompanyRequest, ArchiveCompanyResponse } from "api/companies/archiveCompany";
import { RestoreCompanyRequest, RestoreCompanyResponse } from "api/companies/restoreCompany";
import { BlockCompanyRequest, BlockCompanyResponse } from "api/companies/blockCompany";
import { UnblockCompanyRequest, UnblockCompanyResponse } from "api/companies/unblockCompany";
import { MarkCheckedRequest, MarkCheckedResponse } from "api/companies/markChecked";
import { UpdateOboardingStatusRequest, UpdateOboardingStatusResponse } from "api/companies/updateOnboardingStatus";
import { GetCommentsRequest, GetCommentsResponse } from "api/companies/comments/getComments";
import { DeleteCommentRequest, DeleteCommentResponse } from "api/companies/comments/deleteComment";
import { UpdateCommentRequest, UpdateCommentResponse } from "api/companies/comments/updateComment";
import { CreateCommentRequest, CreateCommentResponse } from "api/companies/comments/createComment";
import { CreateContactResponse, CreateContactRequest } from "api/companies/contacts/createContact";
import { DeleteContactResponse, DeleteContactRequest } from "api/companies/contacts/deleteContact";
import { GetContactsResponse, GetContactsRequest } from "api/companies/contacts/getContacts";
import { UpdateContactResponse, UpdateContactRequest } from "api/companies/contacts/updateContact";
import { GetOfficesResponse, GetOfficesRequest } from "api/companies/offices/getOffices";
import { CreateOfficeResponse, CreateOfficeRequest } from "api/companies/offices/createOffice";
import { DeleteOfficeResponse, DeleteOfficeRequest } from "api/companies/offices/deleteOffice";
import { UpdateOfficeResponse, UpdateOfficeRequest } from "api/companies/offices/updateOffice";
import { GetUsersRequest, GetUsersResponse } from "api/companies/users/getUsers";
import { GetOrderStatsResponse } from "api/companies/getOrderStats";

const companiesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllCompanies: builder.query<GetAllCompaniesResponse, GetAllCompaniesRequest | void>({
      queryFn: createQueryFn(api.companies.getAllCompanies),
      providesTags: [{ type: "Company", id: "LIST" }],
    }),

    getCompanyById: builder.query<GetCompanyByIdResponse, GetCompanyByIdRequest>({
      queryFn: createQueryFn(api.companies.getCompanyById),
      providesTags: (result, error, { companyId }) => [{ type: "Company", id: companyId }],
    }),

    createCompany: builder.mutation<CreateCompanyResponse, CreateCompanyRequest>({
      queryFn: createQueryFn(api.companies.createCompany),
      invalidatesTags: [{ type: "Company", id: "LIST" }],
    }),

    updateCompany: builder.mutation<UpdateCompanyResponse, UpdateCompanyRequest>({
      queryFn: createQueryFn(api.companies.updateCompany),
      invalidatesTags: (result, error, { id }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id },
      ],
    }),

    deleteCompany: builder.mutation<DeleteCompanyResponse, DeleteCompanyRequest>({
      queryFn: createQueryFn(api.companies.deleteCompany),
      invalidatesTags: (result, error, { companyId }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id: companyId },
      ],
    }),

    getUsers: builder.query<GetUsersResponse, GetUsersRequest>({
      queryFn: createQueryFn(api.companies.getUsers),
      providesTags: (result, error, { companyId }) => [
        { type: "CompanyUser", id: "LIST" }
      ],
    }),

    addUserToCompany: builder.mutation<AddUserToCompanyResponse, AddUserToCompanyRequest>({
      queryFn: createQueryFn(api.companies.addUserToCompany),
      invalidatesTags: (result, error, { companyId, userId }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id: companyId },
        { type: "User", id: userId },
        { type: "User", id: "LIST" },
        { type: "CompanyUser", id: "LIST" },
      ],
    }),

    removeUserFromCompany: builder.mutation<RemoveUserFromCompanyResponse, RemoveUserFromCompanyRequest>({
      queryFn: createQueryFn(({ companyId, userId }) => api.companies.removeUserFromCompany({ companyId, userId })),
      invalidatesTags: (result, error, { companyId, userId }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id: companyId },
        { type: "User", id: userId },
        { type: "User", id: "LIST" },
        { type: "CompanyUser", id: "LIST" },
      ],
    }),

    getLifecycleEvents: builder.query<GetLifecycleEventsResponse, GetLifecycleEventsRequest>({
      queryFn: createQueryFn(({ id }) => api.companies.getLifecycleEvents({ id })),
      providesTags: (result, error, { id }) => [{ type: "Company", id }],
    }),

    approveCompany: builder.mutation<ApproveCompanyResponse, ApproveCompanyRequest>({
      queryFn: createQueryFn(api.companies.approveCompany),
      invalidatesTags: (result, error, { companyId }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id: companyId },
      ],
    }),

    denyCompany: builder.mutation<DenyCompanyResponse, DenyCompanyRequest>({
      queryFn: createQueryFn(api.companies.denyCompany),
      invalidatesTags: (result, error, { companyId }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id: companyId },
      ],
    }),

    archiveCompany: builder.mutation<ArchiveCompanyResponse, ArchiveCompanyRequest>({
      queryFn: createQueryFn(api.companies.archiveCompany),
      invalidatesTags: (result, error, { companyId }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id: companyId },
      ],
    }),

    restoreCompany: builder.mutation<RestoreCompanyResponse, RestoreCompanyRequest>({
      queryFn: createQueryFn(api.companies.restoreCompany),
      invalidatesTags: (result, error, { companyId }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id: companyId },
      ],
    }),

    blockCompany: builder.mutation<BlockCompanyResponse, BlockCompanyRequest>({
      queryFn: createQueryFn(api.companies.blockCompany),
      invalidatesTags: (result, error, { companyId }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id: companyId },
      ],
    }),

    unblockCompany: builder.mutation<UnblockCompanyResponse, UnblockCompanyRequest>({
      queryFn: createQueryFn(api.companies.unblockCompany),
      invalidatesTags: (result, error, { companyId }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id: companyId },
      ],
    }),

    updateOnboardingStatus: builder.mutation<UpdateOboardingStatusResponse, UpdateOboardingStatusRequest>({
      queryFn: createQueryFn(api.companies.updateOboardingStatus),
      invalidatesTags: (result, error, { id }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id: id },
      ],
    }),

    markChecked: builder.mutation<MarkCheckedResponse, MarkCheckedRequest>({
      queryFn: createQueryFn(api.companies.markChecked),
      invalidatesTags: (result, error, { id }) => [
        { type: "Company", id: "LIST" },
        { type: "Company", id: id },
      ],
    }),

    getComments: builder.query<GetCommentsResponse, GetCommentsRequest>({
      queryFn: createQueryFn(api.companies.getComments),
      providesTags: () => [{ type: "CompanyComment", id: "LIST" }],
    }),

    createComment: builder.mutation<CreateCommentResponse, CreateCommentRequest>({
      queryFn: createQueryFn(api.companies.createComment),
      invalidatesTags: () => [{ type: "CompanyComment", id: "LIST" }],
    }),

    updateComment: builder.mutation<UpdateCommentResponse, UpdateCommentRequest>({
      queryFn: createQueryFn(api.companies.updateComment),
      invalidatesTags: (result, error, { id }) => [
        { type: "CompanyComment", id: "LIST" },
        { type: "CompanyComment", id: id },
      ],
    }),

    deleteComment: builder.mutation<DeleteCommentResponse, DeleteCommentRequest>({
      queryFn: createQueryFn(api.companies.deleteComment),
      invalidatesTags: (result, error, { id }) => [
        { type: "CompanyComment", id: "LIST" },
        { type: "CompanyComment", id: id },
      ],
    }),

    getContacts: builder.query<GetContactsResponse, GetContactsRequest>({
      queryFn: createQueryFn(api.companies.getContacts),
      providesTags: () => [{ type: "CompanyContact", id: "LIST" }],
    }),

    createContact: builder.mutation<CreateContactResponse, CreateContactRequest>({
      queryFn: createQueryFn(api.companies.createContact),
      invalidatesTags: () => [{ type: "CompanyContact", id: "LIST" }],
    }),

    updateContact: builder.mutation<UpdateContactResponse, UpdateContactRequest>({
      queryFn: createQueryFn(api.companies.updateContact),
      invalidatesTags: (result, error, { id }) => [
        { type: "CompanyContact", id: "LIST" },
        { type: "CompanyContact", id: id },
      ],
    }),

    deleteContact: builder.mutation<DeleteContactResponse, DeleteContactRequest>({
      queryFn: createQueryFn(api.companies.deleteContact),
      invalidatesTags: (result, error, { id }) => [
        { type: "CompanyContact", id: "LIST" },
        { type: "CompanyContact", id: id },
      ],
    }),

    getOffices: builder.query<GetOfficesResponse, GetOfficesRequest>({
      queryFn: createQueryFn(api.companies.getOffices),
      providesTags: () => [{ type: "CompanyOffice", id: "LIST" }],
    }),

    createCompanyOffice: builder.mutation<CreateOfficeResponse, CreateOfficeRequest>({
      queryFn: createQueryFn(api.companies.createOffice),
      invalidatesTags: () => [{ type: "CompanyOffice", id: "LIST" }],
    }),

    updateCompanyOffice: builder.mutation<UpdateOfficeResponse, UpdateOfficeRequest>({
      queryFn: createQueryFn(api.companies.updateOffice),
      invalidatesTags: (result, error, { id }) => [
        { type: "CompanyOffice", id: "LIST" },
        { type: "CompanyOffice", id: id },
      ],
    }),

    deleteCompanyOffice: builder.mutation<DeleteOfficeResponse, DeleteOfficeRequest>({
      queryFn: createQueryFn(api.companies.deleteOffice),
      invalidatesTags: (result, error, { id }) => [
        { type: "CompanyOffice", id: "LIST" },
        { type: "CompanyOffice", id: id },
      ],
    }),

    getCompanyOrderStats: builder.query<GetOrderStatsResponse, void>({
      queryFn: createQueryFn(api.companies.getOrderStats),
      keepUnusedDataFor: 3600, // 1 hour
      providesTags: () => [{ type: "CompanyOrderStats", id: "LIST" }],
    }),
  }),
});

export const {
  useGetAllCompaniesQuery,
  useGetCompanyByIdQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useAddUserToCompanyMutation,
  useRemoveUserFromCompanyMutation,
  useGetLifecycleEventsQuery,
  useApproveCompanyMutation,
  useDenyCompanyMutation,
  useArchiveCompanyMutation,
  useRestoreCompanyMutation,
  useBlockCompanyMutation,
  useUnblockCompanyMutation,
  useUpdateOnboardingStatusMutation,
  useMarkCheckedMutation,
  useGetCommentsQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useGetContactsQuery,
  useCreateContactMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
  useGetOfficesQuery,
  useCreateCompanyOfficeMutation,
  useUpdateCompanyOfficeMutation,
  useDeleteCompanyOfficeMutation,
  useGetUsersQuery,
  useGetCompanyOrderStatsQuery,
} = companiesApi;

export default companiesApi;
